import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Bot, Plus, Search, Copy, Trash2, Upload, Tag, Sparkles, X, Database } from "lucide-react";
import { toast } from "sonner";

const STATUS_BADGE = {
    draft: "bg-amber-400/10 border-amber-400/30 text-amber-400",
    active: "bg-emerald-400/10 border-emerald-400/30 text-emerald-400",
    archived: "bg-white/5 border-white/20 text-white/40",
};

const PROVIDERS = [
    { value: "anthropic", label: "Anthropic Claude Sonnet 5.5", model: "claude-sonnet-5-5" },
    { value: "gemini", label: "Google Gemini 2.5 Flash", model: "gemini-2.5-flash" },
];

export default function AgentsPage() {
    const navigate = useNavigate();
    const [items, setItems] = useState([]);
    const [stats, setStats] = useState({ draft: 0, active: 0, archived: 0, total_all: 0 });
    const [loading, setLoading] = useState(true);
    const [q, setQ] = useState("");
    const [status, setStatus] = useState("");
    const [createOpen, setCreateOpen] = useState(false);

    const load = async () => {
        setLoading(true);
        try {
            const params = new URLSearchParams();
            if (q) params.set("q", q);
            if (status) params.set("status", status);
            const { data } = await api.get(`/admin/agents?${params.toString()}`);
            setItems(data.items || []);
            setStats(data.stats || {});
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const duplicate = async (id) => {
        try {
            const { data } = await api.post(`/admin/agents/${id}/duplicate`);
            toast.success(`Duplicated as ${data.slug}`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const remove = async (id, name) => {
        const reason = window.prompt(`Delete agent "${name}"? Provide a reason for the audit log:`);
        if (!reason || reason.length < 3) return;
        try {
            await api.delete(`/admin/agents/${id}?reason=${encodeURIComponent(reason)}`);
            toast.success("Agent deleted");
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        }
    };

    const importFromJson = async () => {
        const txt = window.prompt("Paste an agent export JSON (schema_version: agent.export.v1):");
        if (!txt) return;
        try {
            const payload = JSON.parse(txt);
            const { data } = await api.post(`/admin/agents/import`, payload);
            toast.success(`Imported ${data.slug}`);
            load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || e?.message || "Invalid JSON");
        }
    };

    return (
        <div className="p-6 space-y-6" data-testid="agents-page">
            <div className="flex items-end justify-between">
                <div>
                    <div className="flex items-center gap-2">
                        <Bot className="w-5 h-5 text-[#7C3AED]" strokeWidth={1.6} />
                        <h1 className="font-display text-2xl tracking-tight">AI Agents</h1>
                    </div>
                    <div className="text-sm text-white/50 mt-1 max-w-xl">
                        Reusable, versioned AI services. Each agent has prompts, schemas, model config, and a
                        playground. Once validated, expose them via flows in the orchestration layer.
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <button
                        onClick={importFromJson}
                        data-testid="agents-import"
                        className="px-3 py-2 border border-white/15 rounded-md text-xs font-medium hover:bg-white/5 flex items-center gap-1.5"
                    >
                        <Upload className="w-3.5 h-3.5" /> Import
                    </button>
                    <button
                        onClick={() => setCreateOpen(true)}
                        data-testid="agents-create"
                        className="px-3 py-2 bg-[#7C3AED] text-white rounded-md text-xs font-medium hover:bg-[#6D28D9] flex items-center gap-1.5"
                    >
                        <Plus className="w-3.5 h-3.5" /> New agent
                    </button>
                </div>
            </div>

            {/* KPI cards */}
            <div className="grid grid-cols-4 gap-3">
                {[
                    { label: "Draft", v: stats.draft, color: "text-amber-400" },
                    { label: "Active", v: stats.active, color: "text-emerald-400" },
                    { label: "Archived", v: stats.archived, color: "text-white/40" },
                    { label: "Total", v: stats.total_all, color: "text-[#7C3AED]" },
                ].map((c) => (
                    <div key={c.label} className="border border-white/10 bg-[#0a0a0a] rounded-md p-4">
                        <div className="label-xs text-white/40">{c.label}</div>
                        <div className={`mt-1 font-mono text-2xl ${c.color}`}>{c.v ?? 0}</div>
                    </div>
                ))}
            </div>

            {/* Filters */}
            <div className="flex items-center gap-2">
                <div className="flex items-center bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2 flex-1 max-w-md">
                    <Search className="w-3.5 h-3.5 text-white/40" />
                    <input
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && load()}
                        placeholder="Search agents…"
                        data-testid="agents-search"
                        className="bg-transparent ml-2 outline-none text-sm flex-1"
                    />
                </div>
                <select
                    value={status}
                    onChange={(e) => { setStatus(e.target.value); setTimeout(load, 0); }}
                    data-testid="agents-status-filter"
                    className="bg-[#0a0a0a] border border-white/10 rounded-md px-3 py-2 text-xs"
                >
                    <option value="">All statuses</option>
                    <option value="draft">Draft</option>
                    <option value="active">Active</option>
                    <option value="archived">Archived</option>
                </select>
                <button
                    onClick={load}
                    className="px-3 py-2 border border-white/15 rounded-md text-xs hover:bg-white/5"
                >
                    Apply
                </button>
            </div>

            {/* List */}
            <div className="border border-white/10 bg-[#0a0a0a] rounded-md overflow-hidden">
                <table className="w-full text-sm">
                    <thead className="bg-white/[0.02] border-b border-white/10">
                        <tr className="text-left label-xs text-white/40">
                            <th className="px-5 py-3">Name</th>
                            <th className="px-5 py-3">Status</th>
                            <th className="px-5 py-3">Provider</th>
                            <th className="px-5 py-3">Version</th>
                            <th className="px-5 py-3">Runs</th>
                            <th className="px-5 py-3">Tags</th>
                            <th className="px-5 py-3">Updated</th>
                            <th className="px-5 py-3 text-right">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {loading ? (
                            <tr><td colSpan={8} className="px-5 py-10 text-center text-white/40">Loading…</td></tr>
                        ) : items.length === 0 ? (
                            <tr>
                                <td colSpan={8} className="px-5 py-12 text-center text-white/40 text-sm" data-testid="agents-empty">
                                    No agents yet. Click <span className="text-[#7C3AED]">New agent</span> to create your first AI service.
                                </td>
                            </tr>
                        ) : items.map((a) => (
                            <tr
                                key={a.id}
                                onClick={() => navigate(`/agents/${a.id}`)}
                                data-testid={`agent-row-${a.id}`}
                                className="border-b border-white/5 hover:bg-white/[0.02] cursor-pointer"
                            >
                                <td className="px-5 py-3">
                                    <div className="font-medium text-white flex items-center gap-2">
                                        {a.name}
                                        {a.mongo_tools?.enabled && (
                                            <span className="text-[9px] uppercase tracking-wider px-1.5 py-0.5 rounded border border-[#7C3AED]/40 text-[#C4B5FD] flex items-center gap-1"
                                                title={`MongoDB tools on · ${(a.mongo_tools.databases || []).join(", ")}`} data-testid={`agent-mongo-badge-${a.id}`}>
                                                <Database className="w-2.5 h-2.5" /> MongoDB
                                            </span>
                                        )}
                                    </div>
                                    <div className="font-mono text-[10px] text-white/40">{a.slug}</div>
                                </td>
                                <td className="px-5 py-3">
                                    <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full border ${STATUS_BADGE[a.status] || ""}`}>
                                        {a.status}
                                    </span>
                                </td>
                                <td className="px-5 py-3 text-xs font-mono text-white/70">
                                    {a.config?.provider}:{a.config?.model}
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">
                                    v{a.active_version_no} <span className="text-white/30">/ {a.version_count}</span>
                                </td>
                                <td className="px-5 py-3 font-mono text-xs">{a.run_count || 0}</td>
                                <td className="px-5 py-3">
                                    <div className="flex flex-wrap gap-1">
                                        {(a.tags || []).slice(0, 3).map((t) => (
                                            <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-white/5 border border-white/10 text-white/60 flex items-center gap-1">
                                                <Tag className="w-2.5 h-2.5" /> {t}
                                            </span>
                                        ))}
                                    </div>
                                </td>
                                <td className="px-5 py-3 font-mono text-[10px] text-white/40">
                                    {a.updated_at ? new Date(a.updated_at).toLocaleString() : "—"}
                                </td>
                                <td className="px-5 py-3" onClick={(e) => e.stopPropagation()}>
                                    <div className="flex items-center justify-end gap-1.5">
                                        <button onClick={() => duplicate(a.id)} title="Duplicate" data-testid={`agent-duplicate-${a.id}`}
                                            className="p-1.5 rounded hover:bg-white/10 text-white/70 hover:text-white">
                                            <Copy className="w-3.5 h-3.5" />
                                        </button>
                                        <button onClick={() => remove(a.id, a.name)} title="Delete" data-testid={`agent-delete-${a.id}`}
                                            className="p-1.5 rounded hover:bg-red-500/20 text-white/70 hover:text-red-400">
                                            <Trash2 className="w-3.5 h-3.5" />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {createOpen && (
                <CreateAgentModal
                    onClose={() => setCreateOpen(false)}
                    onCreated={(a) => { setCreateOpen(false); navigate(`/agents/${a.id}`); }}
                />
            )}
        </div>
    );
}

function CreateAgentModal({ onClose, onCreated }) {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [provider, setProvider] = useState("anthropic");
    const [model, setModel] = useState("claude-sonnet-5-5");
    const [tags, setTags] = useState("");
    const [busy, setBusy] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        if (!name.trim()) return toast.error("Name required");
        setBusy(true);
        try {
            const body = {
                name: name.trim(),
                description: description.trim() || null,
                tags: tags.split(",").map((s) => s.trim()).filter(Boolean),
                config: { provider, model, temperature: 0.2, max_tokens: 4096, response_format: "json", context_window: 200000 },
                system_prompt: "You are an AI service for AIService. Return JSON only.",
                user_prompt_template: "## Input\n{{input}}\n\n## Context\n{{context}}\n\nRespond with a single JSON object.",
                input_schema: { type: "object", properties: { input: { type: "string" } } },
                output_schema: { type: "object" },
                tool_access: [],
                status: "draft",
            };
            const { data } = await api.post("/admin/agents", body);
            toast.success(`Agent created: ${data.slug}`);
            onCreated(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e?.response?.data?.detail));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm" onMouseDown={(e)=>{e.currentTarget._dn=e.target===e.currentTarget;}} onMouseUp={(e)=>{if(e.currentTarget._dn&&e.target===e.currentTarget)onClose();e.currentTarget._dn=false;}}>
            <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg bg-[#0a0a0a] border border-white/15 rounded-md" data-testid="create-agent-modal">
                <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
                    <div className="flex items-center gap-2">
                        <Sparkles className="w-4 h-4 text-[#7C3AED]" />
                        <div className="font-display text-lg tracking-tight">New AI Agent</div>
                    </div>
                    <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={submit} className="p-5 space-y-4">
                    <div>
                        <div className="label-xs mb-1.5">Name *</div>
                        <input value={name} onChange={(e) => setName(e.target.value)} data-testid="create-agent-name"
                            placeholder="e.g. Visit Schedule Summarizer"
                            className="w-full bg-[#050505] border border-white/15 rounded-md px-3 py-2 text-sm" />
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Description</div>
                        <textarea value={description} onChange={(e) => setDescription(e.target.value)}
                            placeholder="What does this agent do?"
                            className="w-full bg-[#050505] border border-white/15 rounded-md px-3 py-2 text-sm" rows={3} />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="label-xs mb-1.5">Provider</div>
                            <select value={provider} onChange={(e) => {
                                const p = PROVIDERS.find((x) => x.value === e.target.value);
                                setProvider(p.value); setModel(p.model);
                            }} className="w-full bg-[#050505] border border-white/15 rounded-md px-3 py-2 text-xs">
                                {PROVIDERS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
                            </select>
                        </div>
                        <div>
                            <div className="label-xs mb-1.5">Model</div>
                            <input value={model} onChange={(e) => setModel(e.target.value)}
                                className="w-full bg-[#050505] border border-white/15 rounded-md px-3 py-2 text-xs font-mono" />
                        </div>
                    </div>
                    <div>
                        <div className="label-xs mb-1.5">Tags (comma-separated)</div>
                        <input value={tags} onChange={(e) => setTags(e.target.value)}
                            placeholder="clinical, summarizer"
                            className="w-full bg-[#050505] border border-white/15 rounded-md px-3 py-2 text-xs" />
                    </div>
                    <div className="flex items-center gap-2 pt-3 border-t border-white/10">
                        <button type="submit" disabled={busy} data-testid="create-agent-submit"
                            className="flex-1 bg-[#7C3AED] text-white py-2 rounded-md text-sm font-medium hover:bg-[#6D28D9] disabled:opacity-50">
                            {busy ? "Creating…" : "Create agent"}
                        </button>
                        <button type="button" onClick={onClose}
                            className="px-4 py-2 border border-white/15 rounded-md text-sm hover:bg-white/5">
                            Cancel
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}
